import React from 'react';
import { Shield, FileText, AlertCircle, CheckCircle, Mail, Phone } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-center mb-6">
            <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <FileText className="h-8 w-8" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-center mb-4">Terms of Service</h1>
          <p className="text-xl text-center text-blue-100 max-w-3xl mx-auto">
            OilWellX Platform Agreement
          </p>
          <p className="text-center text-blue-200 mt-4">
            Effective Date: November 29, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Important Notice */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-6 mb-12">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-amber-900 text-lg mb-2">Important Notice</h3>
              <p className="text-amber-800">
                Please read these Terms of Service carefully before using the OilWellX platform. 
                By accessing or using our services, you agree to be bound by these terms.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-blue-600">1</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Acceptance of Terms</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you 
              ("User," "you," or "your") and OilWellX, a division of TGE Oil & Gas Services 
              ("Company," "we," "us," or "our"), governing your access to and use of the OilWellX 
              platform and related services.
            </p>
            <p>
              By creating an account, accessing, or using OilWellX, you acknowledge that you have 
              read, understood, and agree to be bound by these Terms, including our Privacy Policy. 
              If you do not agree, you must not use our services.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-purple-600">2</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Service Description</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <p>
              OilWellX is a comprehensive field operations management platform designed specifically 
              for the oil and gas industry. Our services include:
            </p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Well production tracking and data entry with offline-first capabilities</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Service ticket management and vendor coordination</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Real-time alerts and automated anomaly detection</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Route optimization and GPS-based well check verification</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Payroll and time tracking for field personnel</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Voice-driven data entry and OCR gauge reading</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                <span>Parts inventory and maintenance tracking</span>
              </li>
            </ul>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue any feature or service at any 
              time without prior notice.
            </p>
          </div>
        </section>

        {/* Section 3 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-green-600">3</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Eligibility and Account Registration</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">3.1 Eligibility</h3>
            <p>
              You must be at least 18 years old and have the legal authority to enter into binding 
              contracts to use OilWellX. By registering, you represent and warrant that all 
              information provided is accurate and current.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6">3.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul className="ml-6 space-y-2">
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Ensuring your account information remains accurate and up-to-date</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">3.3 Multi-Tenant Architecture</h3>
            <p>
              OilWellX operates on a multi-tenant platform where each operator company has its own 
              isolated data environment. You agree not to access or attempt to access data belonging 
              to other tenant organizations.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-rose-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-rose-600">4</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">User Responsibilities</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">4.1 Acceptable Use</h3>
            <p>You agree to use OilWellX only for lawful purposes and in compliance with these Terms. You will not:</p>
            <ul className="ml-6 space-y-2">
              <li>Violate any applicable local, state, federal, or international laws</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the platform's functionality</li>
              <li>Use automated systems to scrape or harvest data</li>
              <li>Impersonate another user or entity</li>
              <li>Share your account credentials with unauthorized persons</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">4.2 Data Accuracy</h3>
            <p>
              You are responsible for the accuracy and completeness of all data entered into the 
              platform, including production volumes, well information, maintenance records, and 
              financial data. We are not liable for decisions made based on inaccurate user input.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">4.3 Safety and Compliance</h3>
            <p>
              OilWellX is a management tool and does not replace proper safety protocols, regulatory 
              compliance, or professional judgment in field operations. Users remain solely responsible 
              for workplace safety and compliance with OSHA, EPA, and other applicable regulations.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-indigo-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-indigo-600">5</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Subscription and Payment</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">5.1 Subscription Plans</h3>
            <p>
              OilWellX offers various subscription tiers based on feature access, user count, and 
              data storage. Pricing is available at{' '}
              <a href="https://oilwellx.com/pricing" className="text-blue-600 hover:underline">
                oilwellx.com/pricing
              </a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">5.2 Billing</h3>
            <ul className="ml-6 space-y-2">
              <li>Subscriptions are billed monthly or annually in advance</li>
              <li>Payment is due immediately upon subscription or renewal</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>We reserve the right to change pricing with 30 days' notice</li>
              <li>Failure to pay may result in service suspension or termination</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">5.3 Free Trial</h3>
            <p>
              New customers may receive a free trial period. We may require payment information 
              upfront. Unless cancelled before the trial ends, you will be automatically charged 
              for the selected subscription plan.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-cyan-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-cyan-600">6</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Data Ownership and Privacy</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">6.1 Your Data</h3>
            <p>
              You retain all ownership rights to data you submit to OilWellX, including well 
              production data, maintenance records, photos, voice memos, and business information. 
              We claim no intellectual property rights over your content.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">6.2 License to Us</h3>
            <p>
              You grant us a limited, worldwide, non-exclusive license to host, store, process, and 
              display your data solely for the purpose of providing and improving our services.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">6.3 Data Privacy</h3>
            <p>
              Our collection and use of personal information is governed by our{' '}
              <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>. 
              Key points include:
            </p>
            <ul className="ml-6 space-y-2">
              <li><strong>We never share your data with third parties</strong> for marketing or other purposes</li>
              <li>Data is encrypted in transit and at rest</li>
              <li>Multi-tenant isolation ensures complete data separation between companies</li>
              <li>You can export your data at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">6.4 Data Backup and Recovery</h3>
            <p>
              While we maintain regular backups, you are responsible for maintaining your own backup 
              copies of critical data. We are not liable for any data loss.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-amber-600">7</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Communications and Consent</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">7.1 Email Communications</h3>
            <p>
              By using OilWellX, you consent to receive emails from us, including:
            </p>
            <ul className="ml-6 space-y-2">
              <li>Account notifications and security alerts</li>
              <li>Service updates and new feature announcements</li>
              <li>Billing and payment confirmations</li>
              <li>System maintenance notifications</li>
              <li>Educational content and best practices</li>
            </ul>
            <p>
              You may opt out of non-essential emails via your account settings, but you cannot 
              opt out of critical service-related communications.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">7.2 SMS Text Messaging Consent</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 my-4">
              <p className="font-semibold text-blue-900 mb-3">
                By providing your mobile phone number and opting in to SMS notifications, you expressly 
                consent to receive automated SMS text messages from OilWellX at the phone number you provide.
              </p>
              <p className="text-gray-700 mb-3">SMS messages may include:</p>
              <ul className="ml-6 space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Well alert notifications (downtime, pressure anomalies, production issues)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Service ticket updates and vendor coordination</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Vendor invitation and onboarding messages</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Time-sensitive operational alerts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Two-factor authentication codes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Service reminders and scheduled maintenance notifications</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100 text-sm text-gray-700 space-y-2">
                <p><strong>Message Frequency:</strong> Varies based on your operations and alert settings. You may receive multiple messages per day during critical events.</p>
                <p><strong>Message & Data Rates:</strong> Standard message and data rates may apply from your mobile carrier.</p>
                <p><strong>Opt-Out:</strong> Reply <strong>STOP</strong> to any message to unsubscribe from SMS alerts. You will receive a confirmation message. Note: Opting out may limit your ability to use certain time-sensitive features.</p>
                <p><strong>Help:</strong> Reply <strong>HELP</strong> to any message for assistance or contact <a href="mailto:support@oilwellx.com" className="text-blue-600 hover:underline">support@oilwellx.com</a></p>
                <p><strong>SMS Number:</strong> Messages are sent from <strong>+1 (855) 301-3570</strong></p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">7.3 Carrier Information & Compliance</h3>
            <p>
              SMS messages are sent via Twilio, a certified telecommunications provider. Supported carriers 
              include AT&T, Verizon, T-Mobile, Sprint, U.S. Cellular, and other major U.S. wireless providers. 
              By consenting to SMS communications, you certify that:
            </p>
            <ul className="ml-6 space-y-2 mt-3">
              <li>You are the authorized user of the mobile phone number provided</li>
              <li>You are at least 18 years of age or have parental consent</li>
              <li>You agree to notify us immediately if you change phone numbers</li>
            </ul>
            <p className="mt-3">
              For SMS technical support, contact <a href="mailto:sms-support@oilwellx.com" className="text-blue-600 hover:underline">sms-support@oilwellx.com</a>.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-red-600">8</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Intellectual Property</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <p>
              The OilWellX platform, including all software, code, designs, logos, trademarks, and 
              content, is the exclusive property of TGE Oil & Gas Services and is protected by 
              copyright, trademark, and other intellectual property laws.
            </p>
            <p>You may not:</p>
            <ul className="ml-6 space-y-2">
              <li>Copy, modify, or create derivative works of our software</li>
              <li>Reverse engineer, decompile, or disassemble the platform</li>
              <li>Remove or alter any proprietary notices</li>
              <li>Use our trademarks without written permission</li>
              <li>Frame or mirror any portion of the platform</li>
            </ul>
          </div>
        </section>

        {/* Section 9 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-orange-600">9</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Disclaimers and Limitation of Liability</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">9.1 "AS IS" Basis</h3>
            <p className="uppercase font-semibold">
              OilWellX is provided "as is" and "as available" without warranties of any kind, 
              express or implied, including but not limited to warranties of merchantability, 
              fitness for a particular purpose, or non-infringement.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">9.2 No Guarantee of Uptime</h3>
            <p>
              While we strive for 99.9% uptime, we do not guarantee uninterrupted or error-free 
              service. The platform may be unavailable due to maintenance, updates, or circumstances 
              beyond our control.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">9.3 Limitation of Liability</h3>
            <p className="uppercase font-semibold">
              To the maximum extent permitted by law, TGE Oil & Gas Services shall not be liable 
              for any indirect, incidental, special, consequential, or punitive damages, or any 
              loss of profits, revenue, data, or use, whether arising from contract, tort, or 
              otherwise, even if advised of the possibility of such damages.
            </p>
            <p className="font-semibold">
              Our total liability for any claim arising from these Terms or your use of OilWellX 
              shall not exceed the amount you paid us in the 12 months preceding the claim, or 
              $500, whichever is greater.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">9.4 Field Operations Risk</h3>
            <p>
              OilWellX is a management tool and does not replace sound operational judgment, 
              safety protocols, or regulatory compliance. We are not responsible for field 
              accidents, equipment failures, environmental incidents, or production losses.
            </p>
          </div>
        </section>

        {/* Section 10 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-teal-600">10</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Indemnification</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <p>
              You agree to indemnify, defend, and hold harmless TGE Oil & Gas Services, its 
              affiliates, officers, directors, employees, and agents from any claims, liabilities, 
              damages, losses, or expenses (including reasonable attorneys' fees) arising from:
            </p>
            <ul className="ml-6 space-y-2">
              <li>Your use or misuse of OilWellX</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any data you submit to the platform</li>
              <li>Field operations, safety incidents, or regulatory violations</li>
            </ul>
          </div>
        </section>

        {/* Section 11 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-violet-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-violet-600">11</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Termination</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">11.1 By You</h3>
            <p>
              You may cancel your subscription at any time via your account settings. Cancellation 
              takes effect at the end of your current billing period. No refunds for partial periods.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">11.2 By Us</h3>
            <p>We may suspend or terminate your account immediately if you:</p>
            <ul className="ml-6 space-y-2">
              <li>Violate these Terms</li>
              <li>Fail to pay subscription fees</li>
              <li>Engage in fraudulent activity</li>
              <li>Pose a security risk to our systems</li>
              <li>Use the platform for illegal purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">11.3 Effect of Termination</h3>
            <p>
              Upon termination, your access to OilWellX will cease immediately. You may export your 
              data within 30 days. After 30 days, we may delete your data permanently. Provisions 
              regarding intellectual property, indemnification, and limitation of liability survive 
              termination.
            </p>
          </div>
        </section>

        {/* Section 12 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-pink-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-pink-600">12</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Dispute Resolution</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">12.1 Informal Resolution</h3>
            <p>
              Before filing a claim, you agree to contact us at{' '}
              <a href="mailto:legal@oilwellx.com" className="text-blue-600 hover:underline">
                legal@oilwellx.com
              </a>{' '}
              to attempt informal resolution.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">12.2 Arbitration</h3>
            <p>
              Any dispute arising from these Terms shall be resolved through binding arbitration 
              in accordance with the American Arbitration Association (AAA) rules. Arbitration 
              shall take place in Texas. You waive your right to a jury trial.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">12.3 Class Action Waiver</h3>
            <p className="font-semibold">
              You agree to resolve disputes on an individual basis only. You waive the right to 
              participate in class actions, class arbitrations, or representative proceedings.
            </p>
          </div>
        </section>

        {/* Section 13 */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="h-12 w-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-gray-600">13</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">General Provisions</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">13.1 Governing Law</h3>
            <p>
              These Terms are governed by the laws of the State of Texas, without regard to conflict 
              of law principles.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">13.2 Modifications</h3>
            <p>
              We may update these Terms at any time. We will notify you via email or platform 
              notification 30 days before changes take effect. Continued use after changes constitutes 
              acceptance.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">13.3 Severability</h3>
            <p>
              If any provision is found unenforceable, the remaining provisions remain in full effect.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">13.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms without our written consent. We may assign 
              our rights and obligations at any time.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">13.5 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between 
              you and TGE Oil & Gas Services regarding OilWellX.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <Mail className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Legal & Terms Questions</p>
                <a href="mailto:legal@oilwellx.com" className="text-blue-200 hover:underline">
                  legal@oilwellx.com
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Support</p>
                <a href="mailto:support@oilwellx.com" className="text-blue-200 hover:underline">
                  support@oilwellx.com
                </a>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="font-semibold mb-2">TGE LLC - Oil & Gas Services Division</p>
            <p className="text-blue-200">
              OilWellX Platform<br />
              Los Angeles, California<br />
              United States
            </p>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pb-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <p className="text-gray-600 mb-4">
              Last Updated: November 29, 2025
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/privacy-policy" className="text-blue-600 hover:underline font-medium">
                Privacy Policy
              </a>
              <span className="text-gray-400">|</span>
              <a href="/vendor/register" className="text-blue-600 hover:underline font-medium">
                Vendor Registration
              </a>
              <span className="text-gray-400">|</span>
              <a href="mailto:legal@oilwellx.com" className="text-blue-600 hover:underline font-medium">
                Legal Contact
              </a>
              <span className="text-gray-400">|</span>
              <a href="/" className="text-blue-600 hover:underline font-medium">
                Return to App
              </a>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              &copy; 2025-2026 TGE LLC. All rights reserved.<br />
              OilWellX is a registered software application of TGE LLC.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
